/**
 * @author: Corey Sanford <corey@hackcapital.com>
 * @date:   2019-01-04 10:14:38
 *
 * Given some relative date filters, create a feathers query object
 *
 * Different rules exist for each of the operators
 */

import dateFns from 'date-fns';
import { QueryBuilderBase } from './QueryBuilderBase';

export class DatetimeQueryBuilder extends QueryBuilderBase {

  constructor(operator, query) {
    super(operator, query);
    this.setCurrentTimes();
  }

  static get Operators() {
    return {
      MORE_THAN_X_DAYS_AGO: {
        name: 'more than x days ago',
        isRelative: true,
        needIntegerValue: true,
      },
      WITHIN_LAST_X_DAYS: {
        name: 'within the last x days',
        isRelative: true,
        needIntegerValue: true,
      },
      YESTERDAY: {
        name: 'yesterday',
        isRelative: true,
      },
      TODAY: {
        name: 'today',
        isRelative: true,
      },
      TOMORROW: {
        name: 'tomorrow',
        isRelative: true,
      },
      WITHIN_NEXT_X_DAYS: {
        name: 'within the next x days',
        isRelative: true,
        needIntegerValue: true,
      },
      MORE_THAN_X_DAYS_FROM_NOW: {
        name: 'more than x days from now',
        isRelative: true,
        needIntegerValue: true,
      },
      OCCURRED_BEFORE: {
        name: 'occurred before',
        isRelative: false,
        needDateValue: true,
      },
      OCCURRED_ON: {
        name: 'occurred on',
        isRelative: false,
        needDateValue: true,
      },
      OCCURRED_AFTER: {
        name: 'occurred after',
        isRelative: false,
        needDateValue: true,
      },
      NEVER_OCCURRED: {
        name: 'is not present',
        isRelative: false,
      },
      HAS_OCCURRED: {
        name: 'is present',
        isRelative: false,
      },
    };
  }

  /**
   * Set times based on this timezone and the day boundaries.
   */
  setCurrentTimes() {
    this.currentTime = new Date();
    this.timezoneOffset = this.currentTime.getTimezoneOffset();
    this.startOfToday = dateFns.startOfDay(this.currentTime);
    this.endOfToday = dateFns.endOfDay(this.currentTime);
  }

  buildCondition(fieldName, operand, operator) {
    let date;
    const condition = {};

    let previousQueryByField = condition[fieldName];

    switch (operator) {
      case this.constructor.Operators.MORE_THAN_X_DAYS_AGO.name:
        condition[fieldName] = {
          $lt: this.startOfPastDay(operand).toISOString(),
          $gt: 0,
        };
        break;
      case this.constructor.Operators.WITHIN_LAST_X_DAYS.name:
        condition[fieldName] = {
          $gte: this.startOfPastDay(operand).toISOString(),
          $lt: this.currentTime.toISOString(),
        };
        break;
      case this.constructor.Operators.YESTERDAY.name:
        condition[fieldName] = {
          $gte: this.startOfYesterday.toISOString(),
          $lt: this.startOfToday.toISOString(),
        };
        break;
      case this.constructor.Operators.TODAY.name:
        condition[fieldName] = {
          $gte: this.startOfToday.toISOString(),
          $lte: this.endOfToday.toISOString(),
        };
        break;
      case this.constructor.Operators.TOMORROW.name:
        condition[fieldName] = {
          $gt: this.endOfToday.toISOString(),
          $lte: this.endOfTomorrow.toISOString(),
        };
        break;
      case this.constructor.Operators.WITHIN_NEXT_X_DAYS.name:
        condition[fieldName] = {
          $gte: this.currentTime.toISOString(),
          $lt: this.endOfFutureDay(operand).toISOString(),
        };
        break;
      case this.constructor.Operators.MORE_THAN_X_DAYS_FROM_NOW.name:
        condition[fieldName] = {
          $gt: this.endOfFutureDay(operand).toISOString(),
        };
        break;
      case this.constructor.Operators.OCCURRED_BEFORE.name:
        date = this.makeLocalDate(new Date(operand));
        if(previousQueryByField){
          condition[fieldName] = {
            ...previousQueryByField,
            $lt: this.startOf(date).toISOString(),
          };
        } else {
          condition[fieldName] = {
            $lt: this.startOf(date).toISOString(),
            // $gt zero because db stores zero as default,
            // but zero timestamp also means 1970 :/
            $gt: 0,
          };
        }
        break;
      case this.constructor.Operators.OCCURRED_ON.name:
        date = this.makeLocalDate(new Date(operand));
        condition[fieldName] = {
          $gte: this.startOf(date).toISOString(),
          $lte: this.endOf(date).toISOString(),
        };
        break;
      case this.constructor.Operators.OCCURRED_AFTER.name:
        date = this.makeLocalDate(new Date(operand));
        condition[fieldName] = {
          ...previousQueryByField,
          $gt: this.endOf(date).toISOString(),
        };
        break;
      case this.constructor.Operators.NEVER_OCCURRED.name:
        condition[fieldName] = {
          $gte: 0,
          $lte: 0,
        };
        break;
      case this.constructor.Operators.HAS_OCCURRED.name:
        condition[fieldName] = {
          $gt: 0,
        };
        break;
      default:
        throw new Error(`Unsupported operator: ${operator}`);
    }

    // Check if the condition is empty
    if (Object.keys(condition).length === 0) {
      return null;
    }

    return condition;
  }

  get startOfYesterday() {
    return this.startOfPastDay(1);
  }

  get endOfTomorrow() {
    return this.endOfFutureDay(1);
  }

  /**
   * Accepts a date like '2018-01-05' and returns a datetime
   * set to the start of the date.
   *
   * @param  {string} date like '2019-01-01'
   * @return {object} some date
   */
  startOf(date) {
    return dateFns.startOfDay(new Date(date));
  }
  endOf(date) {
    return dateFns.endOfDay(new Date(date));
  }

  /**
   * Get the time for the start of a day some number of days
   * in the past.
   *
   * @param  {number} daysInPast - integer denoting the number
   *                               of days in the past
   * @return {object} datetime
   */
  startOfPastDay(daysInPast) {
    return dateFns.subDays(this.startOfToday, daysInPast);
  }

  endOfFutureDay(daysInFuture) {
    return dateFns.addDays(this.endOfToday, daysInFuture);
  }

  /**
   * Convert some date from Local timezone to UTC
   * @param  {object} date to convert
   * @return {object} converted date
   */
  makeUtcDate(date) {
    return dateFns.subMinutes(date, this.timezoneOffset);
  }

  /**
   * Convert some date from UTC to Local timezone
   * @param  {object} date to convert
   * @return {object} converted date
   */
  makeLocalDate(date) {
    return dateFns.addMinutes(date, this.timezoneOffset);
  }

}
